import { useEffect, useRef, useState } from "react";
import { leerCache, escribirCache } from "@/lib/api";

/**
 * Carga un recurso pintando primero la copia guardada.
 *
 * Devuelve `{ data, loading, error, stale }`:
 *   • `data`    lo último conocido (de caché o ya revalidado).
 *   • `loading` solo es cierto cuando NO hay nada que pintar todavía. Si la
 *               caché tenía algo, arranca en `false`: esa es la diferencia
 *               entre ver la página al instante o mirar un spinner.
 *   • `stale`   marca que lo mostrado viene de caché y se está revalidando.
 *
 * @param {string}   clave    Clave de caché. `null` desactiva el guardado.
 * @param {Function} cargar   Función asíncrona que devuelve los datos.
 * @param {Array}    deps     Dependencias que fuerzan una recarga.
 */
export default function useCachedResource(clave, cargar, deps = []) {
  const cacheado = clave ? leerCache(clave) : null;
  const [data, setData] = useState(cacheado);
  const [loading, setLoading] = useState(cacheado == null);
  const [stale, setStale] = useState(cacheado != null);
  const [error, setError] = useState(null);
  const cargarRef = useRef(cargar);
  cargarRef.current = cargar;

  useEffect(() => {
    let cancelado = false;

    // Al cambiar de dependencias (otro slug, otra página) hay que volver a
    // consultar la caché: la del recurso anterior no sirve.
    const previo = clave ? leerCache(clave) : null;
    if (previo != null) {
      setData(previo);
      setLoading(false);
      setStale(true);
    } else {
      setLoading(true);
      setStale(false);
    }

    cargarRef
      .current()
      .then(res => {
        if (cancelado) return;
        setData(res);
        setError(null);
        if (clave) escribirCache(clave, res);
      })
      .catch(err => {
        if (cancelado) return;
        // Si ya se está mostrando algo de caché, un fallo de red no debe
        // borrarlo de la pantalla: es preferible contenido de hace un rato.
        setError(err);
      })
      .finally(() => {
        if (cancelado) return;
        setLoading(false);
        setStale(false);
      });

    return () => {
      cancelado = true;
    };
    // `cargar` se lee desde una ref para no reejecutar el efecto cuando el
    // llamante pasa una función nueva en cada render.
  }, [clave, ...deps]); // eslint-disable-line

  return { data, loading, error, stale };
}
